<script setup lang="ts">
import { FullTableLayout, StackLayout } from '@/components/Layouts'
import { BaseTable, TableColumn, TablePagination } from '@/components/Table'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import { moment } from '@/locales/langUtils'
import { ESortBy, getSortByKey } from '@/types/enum/SortByEnum'
import { type PaginationDataType, defaultPaginationDataType } from '@/types/PaginationType'
import { DATETIME_FORMAT } from '@/utils/dateUtils'
import { getQuerySort } from '@/utils/queryUtils'
import { getPageSize } from '@/utils/routeUtils'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import organizationApi from '../../../../../services/organization.service'
import store from '../../../../../store'
import type { DeviceVersionHistoryModel } from '../../../../../types/models/DeviceVersionHistoryModel'
import { ERentalsPerm } from '../../../../../types/permissions/RentalsPermissionsEnum'
import FullScreenLoader from '../../../../LoadingSpinner/FullScreenLoader.vue'
import TableActionColumn from '../../../../Table/TableActionColumn.vue'

const { t } = useI18n()
const route = useRoute()

type Props = {
    deviceId?: number
}
const props = defineProps<Props>()

const columns = computed(() => ({
    end_at: {
        field: 'end_at',
        label: t('device.rentalsHistory.table.rental_end_at')
    },
    start_at: {
        field: 'start_at',
        label: t('device.rentalsHistory.table.rental_start_at')
    },
    renter: { field: 'renter', label: t('device.rentalsHistory.table.renter') }
}))

const routeOrganizationId = computed(() =>
    route.params.organization_id ? parseInt(route.params.organization_id?.toString()) : undefined
)
const organizationId = computed(() => store.state.authentication.me.organization_id)

const sort = ref<ESortBy>(getQuerySort() ?? ESortBy.DATE_RECENT)
const isLoading = ref(false)

const pagination = ref<PaginationDataType<DeviceVersionHistoryModel[]>>(defaultPaginationDataType)
const getHistory = () => {
    if (!props.deviceId || (!routeOrganizationId.value && !organizationId.value)) return
    isLoading.value = true
    organizationApi
        .getDeviceRentalsHistory(
            routeOrganizationId.value ?? organizationId.value!,
            props.deviceId,
            {
                sort: getSortByKey(sort.value),
                per_page: getPageSize() ?? pagination.value?.per_page,
                page: pagination.value?.current_page
            }
        )
        .then(res => (pagination.value = res))
        .finally(() => (isLoading.value = false))
}

const deleteRental = (rentalId: number) => {
    if (!props.deviceId || (!routeOrganizationId.value && !organizationId.value)) return
    isLoading.value = true
    organizationApi
        .endDeviceRental(
            routeOrganizationId.value ?? organizationId.value!,
            props.deviceId,
            rentalId
        )
        .then(() => getHistory())
        .finally(() => (isLoading.value = false))
}

const handleChangePageSize = (value: number) => {
    pagination.value = { ...pagination.value, current_page: 1, per_page: value }
    getHistory()
}
const handleChangePageNumber = (value: number) => {
    pagination.value = { ...pagination.value, current_page: value }
    getHistory()
}
const handleChangeSortBy = (sortBy: ESortBy) => {
    sort.value = sortBy
    getHistory()
}

const table = ref()

watch(
    () => props.deviceId,
    newDeviceId => (newDeviceId ? getHistory() : undefined),
    { immediate: true }
)
</script>

<template>
    <StackLayout direction="column" class="full-width" :gap="8">
        <FullScreenLoader :isLoading="isLoading" />
        <FullTableLayout :sortBy="sort" @changeSortBy="handleChangeSortBy" class="device-history">
            <template #table>
                <BaseTable
                    v-bind:ref="table"
                    :data="pagination.data"
                    :loading="pagination.isLoading">
                    <template #columns>
                        <TableColumn
                            :field="columns.start_at.field"
                            :label="columns.start_at.label"
                            v-slot="props">
                            {{
                                props.row.start_at
                                    ? moment(props.row.start_at).format(DATETIME_FORMAT).toString()
                                    : $t('common.undefined')
                            }}
                        </TableColumn>
                        <TableColumn
                            :field="columns.end_at.field"
                            :label="columns.end_at.label"
                            v-slot="props">
                            {{
                                props.row.end_at
                                    ? moment(props.row.end_at).format(DATETIME_FORMAT).toString()
                                    : $t('common.undefined')
                            }}
                        </TableColumn>
                        <TableColumn
                            :field="columns.renter.field"
                            :label="columns.renter.label"
                            v-slot="props">
                            <TypographyItem :label="props.row.renter.name" />
                        </TableColumn>
                        <TableActionColumn
                            hideEdit
                            @delete="deleteRental($event?.id)"
                            :deletePermission="ERentalsPerm.RENTALS_DELETE" />
                    </template>
                </BaseTable>
            </template>
            <template #pagination>
                <TablePagination
                    :perPage="pagination.per_page"
                    :currentPage="pagination.current_page"
                    :lastPage="pagination.last_page"
                    :total="pagination.total"
                    @change:pageSize="handleChangePageSize"
                    @change:pageNumber="handleChangePageNumber" />
            </template>
        </FullTableLayout>
    </StackLayout>
</template>

<style scoped lang="scss">
.centered-pagination {
    padding: 8px;
}

.device-history {
    & :deep(.stack) {
        flex-direction: row;
    }
}
</style>
